import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    LinearProgress,
    Chip,
    CircularProgress,
    Avatar,
    alpha,
    useTheme
} from '@mui/material';
import {
    EmojiEvents as TrophyIcon,
    TrendingUp as TrendingUpIcon,
    Assignment as AssignmentIcon,
    ArrowForward as ArrowForwardIcon,
    School as SchoolIcon,
    Event as EventIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import StudentLayout from 'components/layout/StudentLayout';
import { useAuth } from 'contexts/AuthContext';
import { getUserAchievements } from 'services/achievementService';
import { getEnrollmentsByUser } from 'services/enrollmentService';
import { getUserSubmissions, getProblemsBySession } from 'services/problemService';
import { getSessionsAcceptingEnrollment, Session } from 'services/sessionService';
import { getLeaderboard } from 'services/userService';
import { StudentAchievement } from 'types/achievement';
import GuardiansCard from 'components/student/GuardiansCard';

interface ProcessedSession extends Session {
    status: 'active' | 'upcoming' | 'past';
    progress: number;
    problemsSolved: number;
    totalProblems: number;
    enrollmentDate: string;
}

function DashboardPageV2() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { authState } = useAuth();
    const { user } = authState;
    const [loading, setLoading] = useState(true);
    const [recentAchievements, setRecentAchievements] = useState<StudentAchievement[]>([]);
    const [allSessions, setAllSessions] = useState<ProcessedSession[]>([]);
    const [availableSessions, setAvailableSessions] = useState<Session[]>([]);
    const [problemStats, setProblemStats] = useState({
        totalSolved: 0,
        correctRate: 0,
        achievementCount: 0,
        totalPoints: 0,
        rank: 0,
        nextRankPoints: 0,
        rankProgress: 0
    });

    useEffect(() => {
        const fetchData = async () => {
            if (!user) return;

            setLoading(true);
            try {
                // Fetch all data in parallel
                const [achievements, enrollments, submissions, enrollmentSessions] = await Promise.all([
                    getUserAchievements(),
                    getEnrollmentsByUser(user.id),
                    getUserSubmissions(),
                    getSessionsAcceptingEnrollment()
                ]);

                // Set achievements
                const sorted = achievements
                    .sort((a, b) => new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime())
                    .slice(0, 3);
                setRecentAchievements(sorted);

                // Process sessions
                if (enrollments?.enrollments) {
                    const processed = await Promise.all(
                        enrollments.enrollments.map(async (enrollment) => {
                            const session = enrollment.session;
                            const now = new Date();
                            const startDate = new Date(session.startDate);
                            const endDate = new Date(session.endDate);

                            const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                            const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                            const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

                            let status: 'active' | 'upcoming' | 'past';
                            if (nowDateOnly >= startDateOnly && nowDateOnly <= endDateOnly) {
                                status = 'active';
                            } else if (nowDateOnly < startDateOnly) {
                                status = 'upcoming';
                            } else {
                                status = 'past';
                            }

                            const problems = await getProblemsBySession(session.id);
                            const sessionSubmissions = submissions.filter(sub =>
                                problems.some(prob => prob.id === sub.problemId)
                            );

                            const problemsSolved = sessionSubmissions.length;
                            const totalProblems = problems.length;
                            const progress = totalProblems > 0 ? Math.round((problemsSolved / totalProblems) * 100) : 0;

                            return {
                                ...session,
                                status,
                                progress,
                                problemsSolved,
                                totalProblems,
                                enrollmentDate: enrollment.enrollmentDate
                            };
                        })
                    );

                    // Group and sort sessions
                    const groupedSessions = processed.reduce((groups: { active: ProcessedSession[], upcoming: ProcessedSession[], past: ProcessedSession[] }, session) => {
                        groups[session.status].push(session);
                        return groups;
                    }, { active: [], upcoming: [], past: [] });

                    groupedSessions.active.sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime());
                    groupedSessions.upcoming.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
                    groupedSessions.past.sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());

                    const allSessionsList = [
                        ...groupedSessions.active,
                        ...groupedSessions.upcoming,
                        ...groupedSessions.past
                    ];

                    setAllSessions(allSessionsList);
                }

                // Filter available sessions
                const enrolledSessionIds = enrollments?.enrollments?.map(e => e.sessionId) || [];
                const now = new Date();
                const availableToEnroll = enrollmentSessions.filter(
                    session => {
                        const hasNotEnded = new Date(session.endDate) >= now;
                        const notEnrolled = !enrolledSessionIds.includes(session.id);
                        return hasNotEnded && notEnrolled;
                    }
                );
                setAvailableSessions(availableToEnroll);

                // Calculate stats with rank
                const totalSolved = submissions.length;
                const correctCount = submissions.filter(sub => sub.isCorrect).length;
                const correctRate = totalSolved > 0 ? Math.round((correctCount / totalSolved) * 100) : 0;

                // Get leaderboard data for points and rank
                try {
                    const leaderboardData = await getLeaderboard(1, 1000);
                    const currentUserData = leaderboardData.users?.find((u) => u.id === user.id);
                    const points = currentUserData?.totalPoints || 0;

                    // Define rank thresholds
                    const rankThresholds = [0, 10, 25, 50, 100, 200, 300, 500, 750, 1000];

                    let rank = 0;
                    for (let i = 0; i < rankThresholds.length; i++) {
                        if (points >= rankThresholds[i]) {
                            rank = i;
                        } else {
                            break;
                        }
                    }

                    const nextRankPoints = rank < rankThresholds.length - 1
                        ? Math.round((rankThresholds[rank + 1] - points) * 100) / 100
                        : 0;

                    const currentThreshold = rankThresholds[rank];
                    const nextThreshold = rank < rankThresholds.length - 1
                        ? rankThresholds[rank + 1]
                        : currentThreshold;

                    const rankProgress = nextThreshold > currentThreshold
                        ? Math.min(100, Math.round(((points - currentThreshold) / (nextThreshold - currentThreshold)) * 100))
                        : 100;

                    setProblemStats({
                        totalSolved,
                        correctRate,
                        achievementCount: achievements.length,
                        totalPoints: points,
                        rank: rank + 1,
                        nextRankPoints,
                        rankProgress
                    });
                } catch (error) {
                    console.error('Error fetching leaderboard data:', error);
                    setProblemStats({
                        totalSolved,
                        correctRate,
                        achievementCount: achievements.length,
                        totalPoints: 0,
                        rank: 0,
                        nextRankPoints: 0,
                        rankProgress: 0
                    });
                }

            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user]);

    const getRankSuffix = (rank: number): string => {
        if (rank >= 11 && rank <= 13) {
            return 'th';
        }
        switch (rank % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    const activeSessions = allSessions.filter(s => s.status === 'active');

    if (loading) {
        return (
            <StudentLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <CircularProgress />
                </Box>
            </StudentLayout>
        );
    }

    return (
        <StudentLayout>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
                {/* Header */}
                <Box
                    sx={{
                        mb: 5,
                        p: 4,
                        borderRadius: 4,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }}
                >
                    <Typography
                        variant="h3"
                        fontWeight="800"
                        sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1
                        }}
                    >
                        Welcome back, {user?.firstName}! 👋
                    </Typography>
                    <Typography variant="h6" color="text.secondary" fontWeight="400">
                        Here's your learning progress at a glance
                    </Typography>
                </Box>

                {/* Stats Grid */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {[
                        {
                            title: 'Problems Solved',
                            value: problemStats.totalSolved,
                            icon: <AssignmentIcon />,
                            color: theme.palette.success.main,
                            subtitle: `${problemStats.correctRate}% correct`
                        },
                        {
                            title: 'Total Points',
                            value: problemStats.totalPoints.toFixed(2),
                            icon: <TrendingUpIcon />,
                            color: theme.palette.warning.main,
                            subtitle: problemStats.rank > 0 ? `Rank ${problemStats.rank}${getRankSuffix(problemStats.rank)}` : 'Unranked'
                        },
                        {
                            title: 'Active Sessions',
                            value: activeSessions.length,
                            icon: <SchoolIcon />,
                            color: theme.palette.primary.main,
                        },
                        {
                            title: 'Achievements',
                            value: problemStats.achievementCount,
                            icon: <TrophyIcon />,
                            color: theme.palette.secondary.main,
                        }
                    ].map((stat, index) => (
                        <Grid item xs={6} md={3} key={index}>
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    background: `linear-gradient(135deg, ${alpha(stat.color, 0.08)} 0%, ${alpha(stat.color, 0.02)} 100%)`,
                                    border: `1px solid ${alpha(stat.color, 0.2)}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 8px 24px ${alpha(stat.color, 0.2)}`,
                                        borderColor: stat.color,
                                    },
                                }}
                            >
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: alpha(stat.color, 0.15),
                                                color: stat.color,
                                                width: 48,
                                                height: 48,
                                            }}
                                        >
                                            {stat.icon}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h5" fontWeight="700" sx={{ color: stat.color, lineHeight: 1 }}>
                                                {stat.value}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" fontWeight="500" sx={{ mt: 0.5 }}>
                                                {stat.title}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {stat.subtitle && (
                                        <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: 'block' }}>
                                            {stat.subtitle}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Main Content Grid */}
                <Grid container spacing={3}>
                    {/* Left Column - Sessions */}
                    <Grid item xs={12} lg={8}>
                        {/* My Sessions */}
                        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h6" fontWeight="bold">
                                        My Sessions
                                    </Typography>
                                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/browse-sessions')}>
                                        View All
                                    </Button>
                                </Box>

                                {allSessions.length === 0 ? (
                                    <Box sx={{ py: 4, textAlign: 'center' }}>
                                        <Typography variant="body1" color="text.secondary" gutterBottom>
                                            You're not enrolled in any sessions.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            onClick={() => navigate('/browse-sessions')}
                                            sx={{ mt: 2 }}
                                        >
                                            Browse Available Sessions
                                        </Button>
                                    </Box>
                                ) : (
                                    allSessions.map((session) => (
                                        <Box
                                            key={session.id}
                                            sx={{
                                                mb: 2,
                                                p: 2.5,
                                                borderRadius: 3,
                                                border: `1px solid ${theme.palette.divider}`,
                                                background: session.status === 'active'
                                                    ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.03)} 0%, ${alpha(theme.palette.success.main, 0.01)} 100%)`
                                                    : 'background.paper',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`
                                                },
                                                '&:last-child': { mb: 0 }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                                                        <Typography variant="h6" fontWeight="600">
                                                            {session.title}
                                                        </Typography>
                                                        {session.status === 'active' && (
                                                            <Chip label="Active" color="success" size="small" sx={{ height: 22 }} />
                                                        )}
                                                        {session.status === 'upcoming' && (
                                                            <Chip label="Upcoming" color="primary" size="small" sx={{ height: 22 }} />
                                                        )}
                                                        {session.status === 'past' && (
                                                            <Chip label="Completed" size="small" sx={{ height: 22 }} />
                                                        )}
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {new Date(session.startDate).toLocaleDateString()} - {new Date(session.endDate).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                                {session.status === 'active' && (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        endIcon={<ArrowForwardIcon />}
                                                        onClick={() => navigate(`/v2/sessions/${session.id}/problems`)}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        {session.progress === 0 ? 'Start' : 'Continue'}
                                                    </Button>
                                                )}

                                                {session.status === 'upcoming' && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disabled
                                                        startIcon={<EventIcon />}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        Starts {new Date(session.startDate).toLocaleDateString()}
                                                    </Button>
                                                )}

                                                {session.status === 'past' && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        endIcon={<AssignmentIcon />}
                                                        onClick={() => navigate(`/sessions/${session.id}/stats`)}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        View Results
                                                    </Button>
                                                )}
                                            </Box>

                                            <Box sx={{ mt: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="body2" color="text.secondary" fontWeight="500">
                                                        Progress
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="600" color={session.progress === 100 ? 'success.main' : 'primary.main'}>
                                                        {session.problemsSolved}/{session.totalProblems} ({session.progress}%)
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={session.progress}
                                                    sx={{
                                                        height: 6,
                                                        borderRadius: 3,
                                                        bgcolor: alpha(theme.palette.action.hover, 0.1),
                                                        '& .MuiLinearProgress-bar': {
                                                            bgcolor: session.progress === 100 ? theme.palette.success.main : theme.palette.primary.main,
                                                            borderRadius: 3
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </CardContent>
                        </Card>

                        {/* Available Sessions */}
                        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h6" fontWeight="bold">
                                        Available Sessions
                                    </Typography>
                                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/browse-sessions')}>
                                        View All
                                    </Button>
                                </Box>

                                {availableSessions.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                        No new sessions available for enrollment at this time.
                                    </Typography>
                                ) : (
                                    availableSessions.slice(0, 3).map((session) => (
                                        <Card
                                            key={session.id}
                                            variant="outlined"
                                            sx={{
                                                mb: 2,
                                                borderRadius: 2,
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    boxShadow: 2
                                                },
                                                '&:last-child': { mb: 0 }
                                            }}
                                        >
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {session.title}
                                                    </Typography>
                                                    <Chip label="Open" color="success" size="small" icon={<EventIcon />} />
                                                </Box>

                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                    {new Date(session.startDate).toLocaleDateString()} - {new Date(session.endDate).toLocaleDateString()}
                                                </Typography>

                                                <Typography variant="body2" sx={{ mb: 2 }} noWrap>
                                                    {session.description}
                                                </Typography>

                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    onClick={() => navigate(`/sessions/${session.id}/enroll`)}
                                                >
                                                    Enroll Now
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    ))
                                )}

                                {availableSessions.length > 3 && (
                                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <Button
                                            onClick={() => navigate('/browse-sessions')}
                                            endIcon={<ArrowForwardIcon />}
                                        >
                                            See {availableSessions.length - 3} More
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Column - Stats & Widgets */}
                    <Grid item xs={12} lg={4}>
                        {/* Rank Progress */}
                        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main, mr: 2 }}>
                                        <TrophyIcon />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight="bold">
                                        {problemStats.rank === 0 ? "Unranked" :
                                            problemStats.rank === 10 ? "Grandmaster" :
                                                `${problemStats.rank}${getRankSuffix(problemStats.rank)} Rank`}
                                    </Typography>
                                </Box>

                                <LinearProgress
                                    variant="determinate"
                                    value={problemStats.rankProgress}
                                    sx={{
                                        height: 10,
                                        borderRadius: 5,
                                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                                        mb: 1,
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: theme.palette.warning.main
                                        }
                                    }}
                                />

                                <Typography variant="body2" color="text.secondary">
                                    {problemStats.rank < 10 ?
                                        `${problemStats.nextRankPoints} more points to reach the next rank` :
                                        "Maximum rank achieved!"}
                                </Typography>
                            </CardContent>
                        </Card>

                        {/* Recent Achievements */}
                        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                    Recent Achievements
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                                    {recentAchievements.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                            No achievements yet. Keep solving problems!
                                        </Typography>
                                    ) : (
                                        recentAchievements.map((achievement) => (
                                            <Box key={achievement.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                                        color: theme.palette.secondary.main
                                                    }}
                                                >
                                                    <TrophyIcon />
                                                </Avatar>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {achievement.achievement?.name || 'Achievement'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {new Date(achievement.awardedAt).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))
                                    )}
                                </Box>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => navigate('/student/achievements')}
                                >
                                    View All Achievements
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Guardians Card */}
                        <Box sx={{ mb: 3 }}>
                            <GuardiansCard />
                        </Box>

                        {/* Need Help Card */}
                        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, mr: 2 }}>
                                        <InfoIcon />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight="bold">
                                        Need Help?
                                    </Typography>
                                </Box>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    If you need assistance with problems or have questions about the platform, we're here to help!
                                </Typography>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={() => navigate('/help')}
                                    sx={{ mb: 1 }}
                                >
                                    View Help Resources
                                </Button>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => navigate('/contact')}
                                >
                                    Contact Support
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </StudentLayout>
    );
}

export default DashboardPageV2;
